import type { SubSegment } from './sub-segments.js';
import { CANONICAL_SUBSEGMENTS, type CanonicalSubsegment } from './taxonomy.js';
import { TARGET_MARKET_CODES, type TargetMarketCode } from './markets.js';

// Reimbursement-pressure panel — the curated reimbursement reference.
//
// Per `subsegment × country` it scores exposure to public-reimbursement /
// tariff change:  pressure = public exposure × (−real tariff Δ).
//
// THIS IS A CURATED, CITED REFERENCE — NOT A LIVE FEED. There is no single
// machine source for "public exposure %" or "real-terms tariff Δ" by
// subsegment; each populated cell is compiled by hand from authoritative
// public sources (NHS/DHSC fee schedules, Assurance Maladie conventions,
// DREES, sector bodies) and carries its own `source` + `vintage`. Real-terms
// = nominal tariff change − CPI. It is Git-versioned like model_export and
// must be refreshed as new rate vintages publish.
//
// Honesty rules baked in:
//   • Only cells with a defensible, citable basis are listed. Everything else
//     (veterinary ≈ 0 % public; diagnostics — no platform data; anything we
//     can't source) is simply absent → rendered as the n/a availability gap.
//   • A cell with real public exposure but no clean ≥2-vintage rate series
//     sets `tariffReal: null` → exposure shows, but direction / pressure are
//     n/a (never invented).
//   • Curated AHEAD of entity ingest (macro / market-entry coverage): a market
//     can appear here before we hold its companies. Currently UK + FR + DE.
//     The observed-margin cross-check only lights up where we ingest entities
//     (UK/FR), so DE cells read "insufficient filed P&L" there — honestly.

export const REIMBURSEMENT_DATA_VERSION = '2026-06-11';

/** When this curated reference should next be refreshed, and the cadence.
 *  Tariff vintages move with annual fee-setting, so review at least annually:
 *  UK — DHSC / local-authority April uplifts + the Autumn Budget; FR — the LFSS
 *  and convention avenants (médicale / kiné / 100% Santé baskets). The panel
 *  footer surfaces version + next-review so staleness is visible, not silent. */
export const REIMBURSEMENT_REVIEW_BY = '2026-12-31';
export const REIMBURSEMENT_REVIEW_CADENCE =
  'Annual — refresh after UK April fee-setting / Autumn Budget and FR LFSS / convention avenants.';

/** How `tariffReal` / `trajectory` are defined, surfaced in the panel footer. */
export const REIMBURSEMENT_BASIS =
  'Real-terms tariff Δ = nominal reimbursement/fee change − CPI. Curated from cited public sources; each cell carries source + vintage. Not a live feed.';

/** Below this public-exposure share a cell is treated as not reimbursement-
 *  exposed (n/a) — matches the panel’s availability gap. */
export const REIMBURSEMENT_EXPOSURE_FLOOR = 0.06;

/** The reimbursement panel's subsegment rows. Deliberately DIVERGES from the
 *  Market Opportunity buckets: VETERINARY is dropped (≈0% public — a dead row
 *  under a reimbursement lens) and PHARMACY is added — the most reimbursement-
 *  exposed subsegment (pharmacies live on dispensing publicly-reimbursed
 *  medicines). Pharmacy is excluded from the opportunity roll-up panel as
 *  chain-consolidated, but that reasoning doesn't apply here. */
// The top 10 by public-reimbursement relevance (avg exposure across markets),
// most-relevant first. Veterinary (~0% public) and Optical (~23%, mostly
// private dispensing) are excluded; acute Hospitals is deliberately out
// (capital-intensive / often public-owned, overlaps Specialist clinics).
export const REIMBURSEMENT_SUBSEGMENTS = [
  { key: 'primary_care', label: 'GP / Primary care' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'dental', label: 'Dental' },
  { key: 'specialist', label: 'Specialist medical' },
  { key: 'diagnostics', label: 'Diagnostics & labs' },
  { key: 'physio', label: 'Physiotherapy & allied' },
  { key: 'resi_elderly', label: 'Residential elderly care' },
  { key: 'homecare', label: 'Domiciliary / home care' },
  { key: 'resi_spec', label: 'Residential specialist care' },
  { key: 'audiology', label: 'Audiology' },
] as const;

export type ReimbursementSubsegment = (typeof REIMBURSEMENT_SUBSEGMENTS)[number]['key'];

/** Canonical sub-segment (`enm.subsegment`) → reimbursement row. This is what
 *  the margin cross-check maps entities onto.
 *
 *  ⚠️ The ROW LIST above is content-bound and stays at 10: every cell of the
 *  curated tariff reference is compiled by hand against those keys, so adding a
 *  row means commissioning the tariff research to fill it, not editing a
 *  crosswalk. Only the entity→row mapping moved (2026-07-20) — off the legacy
 *  `entities.sub_segment` (populated in 6 of 27 markets) and onto the canonical
 *  column, which every market carries. `diagnostics` now genuinely resolves:
 *  the legacy enum had no diagnostics value at all, which is why that row could
 *  only ever be empty.
 *
 *  Exclusions here are a REIMBURSEMENT judgement, not a coverage gap:
 *  veterinary (~0% public), optical (mostly private dispensing), acute
 *  hospitals (capital-intensive / often public-owned), and the non-clinical
 *  supply chain have no meaningful public-tariff exposure to cross-check. */
export const CANONICAL_TO_REIMBURSEMENT_BUCKET: Record<
  CanonicalSubsegment,
  ReimbursementSubsegment | null
> = {
  Dental: 'dental',
  Pharmacy: 'pharmacy',
  'Primary care (GP)': 'primary_care',
  'Specialist medical': 'specialist',
  'Diagnostics & labs': 'diagnostics',
  'Physiotherapy & allied health': 'physio',
  'Residential elderly care': 'resi_elderly',
  'Residential specialist care': 'resi_spec',
  'Domiciliary / home care': 'homecare',
  Audiology: 'audiology',
  // --- excluded from the reimbursement lens ---
  Veterinary: null,
  Optical: null,
  'Hospitals & clinics': null,
  'Pharma wholesale': null,
  'Medical equipment & supplies': null,
  'Dental laboratories & equipment': null,
  'Ambulance / patient transport': null,
  'Other healthcare': null,
};

/** Every reimbursement row must be reachable from at least one canonical
 *  sub-segment — otherwise the panel renders a row nothing can ever populate,
 *  which is precisely the `diagnostics` failure this rewrite fixed. */
export function reimbursementRowsWithoutSource(): ReimbursementSubsegment[] {
  const reachable = new Set(
    CANONICAL_SUBSEGMENTS.map((c) => CANONICAL_TO_REIMBURSEMENT_BUCKET[c]).filter(Boolean),
  );
  return REIMBURSEMENT_SUBSEGMENTS.map((r) => r.key).filter((k) => !reachable.has(k));
}

/** @deprecated Legacy `entities.sub_segment` crosswalk — that column is empty in
 *  21 of 27 markets. Kept only for any caller still on the legacy enum; the
 *  margin cross-check uses CANONICAL_TO_REIMBURSEMENT_BUCKET. */
export const SUBSEGMENT_TO_REIMBURSEMENT_BUCKET: Record<
  SubSegment,
  ReimbursementSubsegment | null
> = {
  dental: 'dental',
  community_pharmacy: 'pharmacy',
  primary_care: 'primary_care',
  specialist_medical: 'specialist',
  mental_health: 'specialist',
  aesthetics: 'specialist',
  fertility: 'specialist',
  physiotherapy: 'physio',
  allied_health: 'physio',
  residential_care_elderly: 'resi_elderly',
  nursing_homes_medical: 'resi_elderly',
  residential_nursing: 'resi_elderly',
  residential_care_mh: 'resi_spec',
  residential_care_other: 'resi_spec',
  social_care: 'homecare',
  audiology: 'audiology',
  // --- excluded from the reimbursement lens ---
  veterinary: null,
  optical: null,
  medical_retail: null,
  healthcare_it: null,
  other_healthcare: null,
  hospitals: null,
  pharma_wholesale: null,
};

export interface ReimbursementCell {
  bucket: ReimbursementSubsegment;
  country: TargetMarketCode;
  /** 0..1 — share of the subsegment’s revenue that is publicly reimbursed. */
  exposure: number;
  /** Most-recent real-terms tariff Δ (YoY %). null = no clean rate series →
   *  direction / pressure render n/a (exposure still shows). */
  tariffReal: number | null;
  /** Cumulative real-terms index (oldest→newest, start 0) for the sparkline.
   *  Empty when `tariffReal` is null. */
  trajectory: number[];
  /** The funding / rate source this cell is compiled from. */
  source: string;
  /** Vintage span of the underlying rate data (em-dash range), or '—'. */
  vintage: string;
  /** One-line basis / caveat. */
  note: string;
}

// NOTE: numbers are conservative, directionally-sourced real-terms figures —
// see each `source`. They encode the documented direction and rough magnitude,
// not audited precision.
export const REIMBURSEMENT_CELLS: ReimbursementCell[] = [
  // ---------------------------------------------------------------- GB ----
  {
    bucket: 'dental',
    country: 'GB',
    exposure: 0.45,
    tariffReal: -3.0,
    trajectory: [0, -9, -16, -19],
    source: 'NHS dental budget (≈£3bn, ~flat nominal) vs CPI — HFMA / NAO / BDA',
    vintage: '2021–2024',
    note: 'NHS ≈£3bn of a ~£7bn UK dental market; UDA value near-frozen a decade (min uplifted to £28 in 2024).',
  },
  {
    bucket: 'resi_elderly',
    country: 'GB',
    exposure: 0.63,
    tariffReal: -2.0,
    trajectory: [0, -4, -6, -4],
    source:
      'LA care-home fee uplifts vs CPI — ONS self-funding 2022/23; King’s Fund; Homecare Association',
    vintage: '2022–2025',
    note: '63% state-funded (ONS). Fee uplifts ran below National-Living-Wage cost growth; partial 2024/25 catch-up via the Market Sustainability Fund.',
  },
  {
    bucket: 'homecare',
    country: 'GB',
    exposure: 0.77,
    tariffReal: -3.0,
    trajectory: [0, -5, -8, -9],
    source: 'LA domiciliary fee rates vs cost — Homecare Association fee reports; Commons Library',
    vintage: '2022–2025',
    note: '77% state-funded. LA-paid rates persistently below the Homecare Association minimum price for sustainable care.',
  },
  {
    bucket: 'resi_spec',
    country: 'GB',
    exposure: 0.85,
    tariffReal: -2.0,
    trajectory: [0, -4, -6, -7],
    source: 'LA + NHS Continuing-Healthcare placement fees vs cost — King’s Fund ASC',
    vintage: '2022–2025',
    note: 'LD / MH specialist residential is largely LA + NHS CHC funded; fee uplifts below cost inflation.',
  },
  {
    bucket: 'primary_care',
    country: 'GB',
    exposure: 0.95,
    tariffReal: -1.5,
    trajectory: [0, -3, -5, -2],
    source:
      'NHS GMS contract — global sum £107.57/weighted patient 2024/25; +1.9% imposed (3rd consecutive real cut) vs CPI; +8.26% 2025/26 — BMA / King’s Fund',
    vintage: '2022–2025',
    note: '~95% NHS. Three consecutive imposed real-terms cuts drove GP collective action; the 2025/26 +8.26% global-sum uplift only part-restores it.',
  },
  {
    bucket: 'specialist',
    country: 'GB',
    exposure: 0.3,
    tariffReal: null,
    trajectory: [],
    source:
      'Blended NHS-funded share (independent-sector mental health / fertility); no single rate series',
    vintage: '—',
    note: 'Aesthetics private; some NHS-funded MH / fertility. No clean tariff trajectory for the blended bucket → direction n/a.',
  },
  {
    bucket: 'physio',
    country: 'GB',
    exposure: 0.3,
    tariffReal: null,
    trajectory: [],
    source: 'NHS MSK / AQP physiotherapy (no published real-terms rate series)',
    vintage: '—',
    note: 'Mixed NHS + private; AQP/community rates not published as a real-terms series → direction n/a.',
  },
  {
    bucket: 'audiology',
    country: 'GB',
    exposure: 0.25,
    tariffReal: null,
    trajectory: [],
    source: 'NHS AQP hearing-aid provision (private dispensing dominates)',
    vintage: '—',
    note: 'Private-pay dominates UK audiology; only the AQP NHS share is exposed → direction n/a.',
  },

  // ---------------------------------------------------------------- FR ----
  {
    bucket: 'specialist',
    country: 'FR',
    exposure: 0.62,
    tariffReal: 4.0,
    trajectory: [0, 0, 3, 8],
    source:
      'Convention médicale 2024–2029 — gynéco €33.5→40, psychiatre €51.7→57, gériatre €31.5→42 (Assurance Maladie)',
    vintage: '2024–2025',
    note: 'Secteur 1 fully reimbursed; €1.6bn revalorisation in Dec-2024 / Jul-2025 after ~a decade near-frozen → tailwind.',
  },
  {
    bucket: 'audiology',
    country: 'FR',
    exposure: 0.78,
    tariffReal: 1.0,
    trajectory: [0, 18, 34, 35],
    source: '100% Santé hearing-aid reform — DREES; hearing-aid sales +75% 2019–21',
    vintage: '2019–2022',
    note: 'Zero-out-of-pocket basket made audiology reimbursement-led; the big tailwind landed 2019–21 and is now structural.',
  },
  {
    bucket: 'dental',
    country: 'FR',
    exposure: 0.58,
    tariffReal: 0.5,
    trajectory: [0, 6, 10, 11],
    source: '100% Santé dental prostheses — DREES; +17% volume, 57% chose the basket',
    vintage: '2020–2023',
    note: 'Assurance Maladie + 100% Santé reimbursed basket; reform raised reimbursed prosthesis tariffs.',
  },
  {
    bucket: 'primary_care',
    country: 'FR',
    exposure: 0.9,
    tariffReal: 5.0,
    trajectory: [0, 0, 5, 12],
    source:
      'Convention médicale 2024–2029 — généraliste consultation €26.50→€30 (Dec-2024) — Assurance Maladie',
    vintage: '2024–2025',
    note: 'Secteur 1 ~fully reimbursed; the €30 consultation (+13% nominal) after years frozen is a clear real-terms tailwind.',
  },
  {
    bucket: 'physio',
    country: 'FR',
    exposure: 0.85,
    tariffReal: -0.5,
    trajectory: [0, -3, -5, -4],
    source: 'Avenant 7 kiné (+3% letter-keys Jan-24) vs CPI — Assurance Maladie; FFMKR',
    vintage: '2013–2024',
    note: 'Heavily reimbursed; the +3% (Jan-24) followed ~11 years frozen — unions call it a real-terms purchasing-power decline.',
  },
  {
    bucket: 'resi_elderly',
    country: 'FR',
    exposure: 0.45,
    tariffReal: 0.5,
    trajectory: [0, -1, 1, 2],
    source:
      'EHPAD soins/dépendance public funding (+5% public EHPAD 2024) vs CPI — FHF / PLFSS 2024',
    vintage: '2023–2024',
    note: '3-part tariff: soins (public/branche Autonomie) + dépendance (départements) + hébergement (resident-paid). Public uplift modest.',
  },
  {
    bucket: 'homecare',
    country: 'FR',
    exposure: 0.7,
    tariffReal: null,
    trajectory: [],
    source: 'SAAD / SSIAD — APA + national tariff floor (€23/h, 2022, branche Autonomie)',
    vintage: '—',
    note: 'Publicly funded via APA / branche Autonomie; recent real-terms trajectory not cleanly sourced → direction n/a.',
  },
  {
    bucket: 'resi_spec',
    country: 'FR',
    exposure: 0.85,
    tariffReal: null,
    trajectory: [],
    source: 'Médico-social handicap (ARS / départements)',
    vintage: '—',
    note: 'Publicly funded; no clean real-terms rate series sourced → direction n/a.',
  },

  // -------------------------------------------------- diagnostics (GB / FR) --
  {
    bucket: 'diagnostics',
    country: 'GB',
    exposure: 0.7,
    tariffReal: null,
    trajectory: [],
    source:
      'NHS pathology / imaging + independent-sector diagnostic contracts (no published real-terms rate series)',
    vintage: '—',
    note: 'Heavily NHS-funded; the Community Diagnostic Centre programme is expanding volume but there is no clean real-terms tariff series → direction n/a.',
  },
  {
    bucket: 'diagnostics',
    country: 'FR',
    exposure: 0.85,
    tariffReal: -4.0,
    trajectory: [0, -9, -18, -17],
    source:
      'Biologie médicale protocole — Uncam tariffs −7% (2023), −4% (Jan-24), −10% (Sep-24); +0.4%/yr envelope 2024–26 — Assurance Maladie',
    vintage: '2023–2025',
    note: 'Lettre-clé B 27→26 cts; ~−11% in a year then a further cut → strong real-terms pressure; stabilised (+1.75%) for 2025–26.',
  },

  // ---------------------------------------------------------------- DE ----
  {
    bucket: 'dental',
    country: 'DE',
    exposure: 0.6,
    tariffReal: -3.0,
    trajectory: [0, -5, -6, -7],
    source:
      'GKV-FinStG Budgetierung — dental fee growth capped +0.75% (2023) / +1.5% (2024), €460m cut vs CPI — KZBV',
    vintage: '2023–2024',
    note: 'GKV dentistry (excl. prosthetics) budget-capped far below cost inflation → pressure; private/PKV is the minority.',
  },
  {
    bucket: 'specialist',
    country: 'DE',
    exposure: 0.65,
    tariffReal: 0.0,
    trajectory: [0, 1, 1, 0],
    source:
      'EBM Orientierungswert (~+3.85% 2024) vs CPI; Fachärzte volume-budgeted (RLV) — KBV / GKV',
    vintage: '2023–2024',
    note: 'GKV statutory; point value roughly tracks inflation amid volume budgeting; the 2027 GKV cost-control reform is a downside risk.',
  },
  {
    bucket: 'diagnostics',
    country: 'DE',
    exposure: 0.85,
    tariffReal: -2.0,
    trajectory: [0, -3, -4, -6],
    source:
      'Laborreform — EBM lab fees cut to ~89% of Euro price (from ~92%) + 2025 transport-cost cuts — Dt. Ärzteblatt / KV',
    vintage: '2018–2025',
    note: 'GKV labs under spending caps with ongoing fee erosion → pressure.',
  },
  {
    bucket: 'physio',
    country: 'DE',
    exposure: 0.85,
    tariffReal: 1.0,
    trajectory: [0, 8, 20, 22],
    source:
      'Heilmittelreform TSVG — federal uniform prices 2019 + arbitration +14.09% Aug-2021 — GKV / Physio Deutschland',
    vintage: '2019–2022',
    note: 'GKV Heilmittel; the reform sharply raised physiotherapy tariffs 2019–21 → structural tailwind.',
  },
  {
    bucket: 'resi_elderly',
    country: 'DE',
    exposure: 0.45,
    tariffReal: -0.5,
    trajectory: [0, -2, -1, 0],
    source:
      'Pflegeversicherung + PUEG 2023; rising Eigenanteil (avg €2,248→€2,871, 2022–24) — BMG / vdek',
    vintage: '2023–2024',
    note: 'Statutory LTC covers part of care cost; resident Eigenanteil rising fast → public share modest, mild squeeze.',
  },
  {
    bucket: 'homecare',
    country: 'DE',
    exposure: 0.7,
    tariffReal: null,
    trajectory: [],
    source: 'Pflegeversicherung (ambulante Sachleistung)',
    vintage: '—',
    note: 'Pflegekasse-funded; recent real-terms trajectory not cleanly sourced → direction n/a.',
  },
  {
    bucket: 'audiology',
    country: 'DE',
    exposure: 0.4,
    tariffReal: null,
    trajectory: [],
    source: 'GKV Festbetrag (zuzahlungsfreie Versorgung since 2013); premium dispensing private',
    vintage: '—',
    note: 'GKV covers a basic device via the Festbetrag; premium fitting is private; no clean recent rate series → direction n/a.',
  },
  {
    bucket: 'resi_spec',
    country: 'DE',
    exposure: 0.85,
    tariffReal: null,
    trajectory: [],
    source: 'Eingliederungshilfe / BTHG (Sozialhilfe)',
    vintage: '—',
    note: 'Publicly funded; no clean real-terms rate series sourced → direction n/a.',
  },
  {
    bucket: 'primary_care',
    country: 'DE',
    exposure: 0.9,
    tariffReal: 3.0,
    trajectory: [0, 0, 2, 8],
    source:
      'Entbudgetierung der Hausärzte (GVSG, 2025) — GP budget cap removed Q4-2025, ~85% of fees now fully paid (+€400m/yr) — BMG / KBV',
    vintage: '2024–2025',
    note: 'After ~30 years of budgeting, removing the Hausarzt budget cap fully reimburses GP services → structural tailwind.',
  },

  // ------------------------------------------------ pharmacy (GB / FR / DE) --
  {
    bucket: 'pharmacy',
    country: 'GB',
    exposure: 0.9,
    tariffReal: -2.5,
    trajectory: [0, -8, -16, -6],
    source:
      'NHS Community Pharmacy CPCF — global sum frozen £2.592bn 2019–24 (~25% below 2015/16 real) vs CPI — CPE / CCA / Pharmaceutical Journal',
    vintage: '2019–2025',
    note: 'Dispensing ~90% NHS. ~£800m cumulative real-terms loss + 13% network contraction (1,479 closures); a 19% nominal uplift in 2025 only part-restores it.',
  },
  {
    bucket: 'pharmacy',
    country: 'FR',
    exposure: 0.82,
    tariffReal: -0.5,
    trajectory: [0, -1, -2, -2],
    source:
      'Officine — honoraires de dispensation (Avenant 1, Jul-2024: first-ever −1.85% fee cut) + reduced regulated margin — Assurance Maladie / Cour des comptes',
    vintage: '2015–2024',
    note: 'Income decoupled from drug price into dispensing fees (2015/19); the 2024 avenant mixed small fee rises with the first-ever 1.85% cut.',
  },
  {
    bucket: 'pharmacy',
    country: 'DE',
    exposure: 0.85,
    tariffReal: -3.0,
    trajectory: [0, -7, -13, -19],
    source:
      'AMPreisV Festzuschlag €8.35 per Rx — frozen since 2013 (€8.10 from 2004) vs CPI — ABDA / GKV-Spitzenverband',
    vintage: '2013–2024',
    note: 'GKV Rx dispensing fee unchanged 12+ years while wage/energy costs rose; pharmacists demand €12 — deep real-terms erosion.',
  },
];

const BY_KEY = new Map<string, ReimbursementCell>(
  REIMBURSEMENT_CELLS.map((c) => [`${c.country}|${c.bucket}`, c]),
);

/** The target markets this reference currently covers, in canonical roster
 *  order — drives the panel's column axis. */
export const REIMBURSEMENT_COUNTRIES: TargetMarketCode[] = TARGET_MARKET_CODES.filter((c) =>
  REIMBURSEMENT_CELLS.some((x) => x.country === c),
);

export function getReimbursementCell(
  bucket: ReimbursementSubsegment,
  country: TargetMarketCode,
): ReimbursementCell | undefined {
  return BY_KEY.get(`${country}|${bucket}`);
}

/** pressure = exposure × max(0, −real tariff Δ); null when direction is n/a. */
export function reimbursementPressure(cell: ReimbursementCell): number | null {
  if (cell.tariffReal == null) return null;
  return cell.exposure * Math.max(0, -cell.tariffReal);
}
